import { useReducer } from 'react';

import type { ValidationMessageLevel } from '@/services/bulk-donor-scheduling';

import type { BulkDonorInput } from '@/pages/bulk-donor-scheduling/utils/schema';

export type Messages = Record<
  string,
  {
    level: ValidationMessageLevel;
    message: string;
    suggestions?: string[];
  }
>;

export type BulkDonorState = (
  | {
      type: 'idle';
      readonly: false;
      loading: false;
      errors: Record<string, string> | undefined;
      messages: Messages | undefined;
    }
  | {
      type: 'client_validating';
      readonly: true;
      loading: true;
      errors: undefined;
      messages: undefined;
    }
  | {
      type: 'server_validating';
      readonly: true;
      loading: true;
      errors: undefined;
      messages: undefined;
    }
  | {
      type: 'server_validated';
      readonly: false;
      loading: false;
      errors: undefined;
      messages: Messages | undefined;
    }
  | {
      type: 'uploading';
      readonly: true;
      loading: true;
      errors: undefined;
      messages: Messages | undefined;
    }
  | {
      type: 'success';
      readonly: true;
      loading: false;
      errors: undefined;
      messages: Messages | undefined;
    }
) & {
  rows: BulkDonorInput[];
};

export const initialState: BulkDonorState = {
  type: 'idle',
  readonly: false,
  loading: false,
  rows: Array.from({ length: 31 }) as any[],
  errors: undefined,
  messages: undefined,
};

type BulkDonorAction =
  | { type: 'reset' }
  | { type: 'idle' }
  | { type: 'add_rows'; count: number }
  | { type: 'set_row'; rowIndex: number; data: BulkDonorInput }
  | { type: 'client_validating' }
  | {
      type: 'client_validated';
      errors: Record<string, string> | undefined;
    }
  | {
      type: 'server_validated';
      errors: Record<string, string> | undefined;
      messages: Messages | undefined;
    }
  | { type: 'uploading' }
  | { type: 'server_error' }
  | { type: 'success' };

export const bulkDonorReducer: React.Reducer<BulkDonorState, BulkDonorAction> = (
  state,
  action,
) => {
  switch (action.type) {
    case 'reset':
      return initialState;
    case 'idle':
      return {
        type: 'idle',
        readonly: false,
        loading: false,
        rows: state.rows,
        errors: undefined,
        messages: undefined,
      };
    case 'add_rows':
      return {
        ...state,
        rows: [
          ...state.rows,
          ...(Array.from({ length: action.count }) as any[]),
        ],
      };
    case 'set_row': {
      const newRows = [...state.rows];
      newRows[action.rowIndex] = action.data;
      return { ...state, rows: newRows };
    }
    case 'client_validating':
      return {
        type: 'client_validating',
        readonly: true,
        loading: true,
        rows: state.rows,
        errors: undefined,
        messages: undefined,
      };
    case 'client_validated':
      if (action.errors) {
        return {
          type: 'idle',
          readonly: false,
          loading: false,
          rows: state.rows,
          errors: action.errors,
          messages: undefined,
        };
      }
      return {
        type: 'server_validating',
        readonly: true,
        loading: true,
        rows: state.rows,
        errors: undefined,
        messages: undefined,
      };
    case 'server_validated':
      if (action.errors) {
        return {
          type: 'idle',
          readonly: false,
          loading: false,
          rows: state.rows,
          errors: action.errors,
          messages: action.messages,
        };
      }
      return {
        type: 'server_validated',
        readonly: false,
        loading: false,
        rows: state.rows,
        errors: undefined,
        messages: action.messages,
      };
    case 'uploading':
      return {
        type: 'uploading',
        readonly: true,
        loading: true,
        rows: state.rows,
        errors: undefined,
        messages: state.type === 'server_validated' ? state.messages : undefined,
      };
    case 'server_error':
      return {
        type: 'idle',
        readonly: false,
        loading: false,
        rows: state.rows,
        errors: undefined,
        messages: undefined,
      };
    case 'success':
      return {
        type: 'success',
        readonly: true,
        loading: false,
        rows: state.rows,
        errors: undefined,
        messages: state.type === 'uploading' ? state.messages : undefined,
      };
    default:
      return state;
  }
};

export function useBulkDonorStore() {
  return useReducer(bulkDonorReducer, initialState);
}
